import { saveJson } from '../../../../services/fileSystemService'
import { Tag } from '../Tags/tagsAdminModel'

/**
 * The case body document stored in eval `eval_case_version` — mirrors the
 * backend `CaseBody` schema (harness/case_spec.py). A body that passes
 * `prepareCaseBody` here is shaped to pass the backend's save-time validation.
 */
export type WriteExpectation = {
  tool: string
  args_match?: Record<string, unknown>
  count?: number
}

export type OutcomeSpec = {
  writes?: WriteExpectation[]
  forbidden_writes?: string[]
  answer_must_contain?: string[]
}

export type TrajectorySpec = {
  must_call?: string[]
  must_not_call?: string[]
  max_tool_calls?: number | null
}

export type JudgeSpec = {
  rubric: string
  threshold: number
}

export type ContextMessage = {
  role: 'user' | 'assistant' | 'tool'
  content?: unknown
  tool_calls?: unknown[]
  tool_call_id?: string
}

export type CaseBody = {
  name?: string | null
  scenario: string
  context: ContextMessage[]
  /**
   * Optional: a case may define a deterministic outcome, a judge, or both, but
   * must have at least one (eval#84). Omitted for judge-only cases.
   */
  outcome?: OutcomeSpec | null
  trajectory?: TrajectorySpec | null
  judge?: JudgeSpec | null
  /** Advisory lineage (#79): the golden prompt this case was seeded from. */
  golden_prompt_id?: string | null
}

/** An eval case — the entity that binds a scenario, a prompt, and its checks. */
export type EvalCase = {
  case_id: string
  name: string
  scenario: string
  version_id: string
  body: CaseBody
  created_at: string
  version_created_at: string
  /** Current (active) entity-level tags (#92), not part of the versioned body. */
  tags: Tag[]
}

/** The list endpoint returns every case (it is not server-paginated). */
export type EvalCaseListResponse = {
  cases: EvalCase[]
  total: number
}

export type EvalCaseCreateInput = {
  name: string
  body: CaseBody
  /** Entity-level tags synced to join rows on create (#92). */
  tag_ids: string[]
}

export type EvalCaseUpdateInput = {
  case_id: string
  body: CaseBody
}

/** Payload for the version-free tag-replace endpoint, PUT /eval-cases/{id}/tags. */
export type EvalCaseTagsUpdateInput = {
  case_id: string
  tag_ids: string[]
}

/** Page size for the client-side paginated case list. */
export const CASES_PAGE_SIZE = 25

/**
 * Editor form state. Genuinely free-form, deeply-nested fields (the message
 * list and the write-expectation matchers) are edited as JSON text; the
 * simple string lists and scalars get structured controls.
 */
export type CaseFormState = {
  name: string
  scenario: string
  /** JSON array of context messages; the last entry must be role: user. */
  contextJson: string
  /** JSON array of WriteExpectation objects. */
  writesJson: string
  forbiddenWrites: string[]
  answerMustContain: string[]
  trajectoryEnabled: boolean
  mustCall: string[]
  mustNotCall: string[]
  maxToolCalls: number | null
  judgeEnabled: boolean
  rubric: string
  threshold: number
  /** The golden prompt the current prompt was seeded from, if any (#79). */
  goldenPromptId: string | null
  /** Selected entity-level tag ids (#92), managed outside the versioned body. */
  tagIds: string[]
}

const DEFAULT_CONTEXT_JSON = JSON.stringify(
  [{ role: 'user', content: '' }],
  null,
  2,
)

export const emptyCaseForm = (): CaseFormState => ({
  name: '',
  scenario: '',
  contextJson: DEFAULT_CONTEXT_JSON,
  writesJson: '[]',
  forbiddenWrites: [],
  answerMustContain: [],
  trajectoryEnabled: false,
  mustCall: [],
  mustNotCall: [],
  maxToolCalls: null,
  judgeEnabled: false,
  rubric: '',
  threshold: 0.7,
  goldenPromptId: null,
  tagIds: [],
})

/** Seed the editor form from an existing case (edit mode). */
export const caseFormFromBody = (
  name: string,
  body: CaseBody,
  tags: Tag[] = [],
): CaseFormState => {
  const outcome = body.outcome ?? {}
  const trajectory = body.trajectory ?? null
  const judge = body.judge ?? null
  return {
    name,
    scenario: body.scenario ?? '',
    contextJson: JSON.stringify(body.context ?? [], null, 2),
    writesJson: JSON.stringify(outcome.writes ?? [], null, 2),
    forbiddenWrites: outcome.forbidden_writes ?? [],
    answerMustContain: outcome.answer_must_contain ?? [],
    trajectoryEnabled: trajectory != null,
    mustCall: trajectory?.must_call ?? [],
    mustNotCall: trajectory?.must_not_call ?? [],
    maxToolCalls: trajectory?.max_tool_calls ?? null,
    judgeEnabled: judge != null,
    rubric: judge?.rubric ?? '',
    threshold: judge?.threshold ?? 0.7,
    goldenPromptId: body.golden_prompt_id ?? null,
    tagIds: tags.map((t) => t.tag_id),
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Read the final context message's content as display text — used to preview
 * the current prompt without unfolding the raw context editor. Returns null
 * when there is no usable prompt (unparseable/empty context, or no content);
 * structured (non-string) content is shown as compact JSON.
 */
export const finalPromptText = (contextJson: string): string | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(contextJson)
  } catch {
    return null
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return null
  }
  const last = parsed[parsed.length - 1]
  if (!isPlainObject(last)) {
    return null
  }
  const content = last.content
  if (typeof content === 'string') {
    return content
  }
  if (content == null) {
    return null
  }
  return JSON.stringify(content)
}

/**
 * Set the content of the final context message to `promptText` — used by the
 * golden-prompt picker. The final entry is, by construction, the user turn that
 * becomes the prompt, so this also pins its role to `user`. If the current
 * context can't be parsed as a non-empty array, it is replaced with a single
 * user message carrying the prompt. Returns pretty-printed JSON for the editor.
 */
export const applyGoldenPromptToContext = (
  contextJson: string,
  promptText: string,
): string => {
  let parsed: unknown
  try {
    parsed = JSON.parse(contextJson)
  } catch {
    parsed = null
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return JSON.stringify([{ role: 'user', content: promptText }], null, 2)
  }
  const last = parsed[parsed.length - 1]
  const finalMessage = {
    ...(isPlainObject(last) ? last : {}),
    role: 'user',
    content: promptText,
  }
  return JSON.stringify([...parsed.slice(0, -1), finalMessage], null, 2)
}

type PreparedBody = { body?: CaseBody; error?: string }

/**
 * Parse the JSON fields, validate against the backend's save-time rules, and
 * assemble the case body. Returns the ready body or a single actionable error
 * message — the same one-error-at-a-time shape the modal surfaces.
 */
export const prepareCaseBody = (form: CaseFormState): PreparedBody => {
  if (!form.scenario.trim()) {
    return { error: 'Scenario is required.' }
  }

  // Context: a non-empty message list whose final entry is role: user.
  let context: unknown
  try {
    context = JSON.parse(form.contextJson)
  } catch {
    return { error: 'Context is not valid JSON.' }
  }
  if (!Array.isArray(context) || context.length === 0) {
    return { error: 'Context must be a non-empty JSON array of messages.' }
  }
  if (!context.every((m) => isPlainObject(m) && typeof m.role === 'string')) {
    return { error: 'Each context message must be an object with a role.' }
  }
  const last = context[context.length - 1] as Record<string, unknown>
  if (last.role !== 'user') {
    return { error: 'The final context message must have role: user.' }
  }
  // The final message is the prompt — guard against saving it blank (e.g. the
  // default empty message when no golden prompt was picked). A structured
  // (non-string) content is left alone.
  const promptContent = last.content
  if (
    promptContent == null ||
    (typeof promptContent === 'string' && promptContent.trim() === '')
  ) {
    return {
      error: 'The prompt (final context message) is empty — pick a golden prompt or fill it in.',
    }
  }

  // Writes: an array of { tool, ... } matchers (args matchers are free-form).
  let writes: unknown
  try {
    writes = JSON.parse(form.writesJson)
  } catch {
    return { error: 'Writes is not valid JSON.' }
  }
  if (!Array.isArray(writes)) {
    return { error: 'Writes must be a JSON array.' }
  }
  if (!writes.every((w) => isPlainObject(w) && typeof w.tool === 'string')) {
    return { error: 'Each write expectation needs a "tool" name.' }
  }

  const outcome: OutcomeSpec = {}
  if (writes.length > 0) {
    outcome.writes = writes as WriteExpectation[]
  }
  if (form.forbiddenWrites.length > 0) {
    outcome.forbidden_writes = form.forbiddenWrites
  }
  if (form.answerMustContain.length > 0) {
    outcome.answer_must_contain = form.answerMustContain
  }
  const outcomeHasCheck = Boolean(
    outcome.writes || outcome.forbidden_writes || outcome.answer_must_contain,
  )

  // A judge only counts as the case's test once its config is complete.
  let judge: JudgeSpec | undefined
  if (form.judgeEnabled) {
    if (!form.rubric.trim()) {
      return { error: 'A judge needs a rubric; pick one or disable the judge.' }
    }
    if (!Number.isFinite(form.threshold)) {
      return { error: 'The judge threshold must be a number.' }
    }
    judge = { rubric: form.rubric.trim(), threshold: form.threshold }
  }

  // At least one test must be defined: a deterministic outcome check or a judge
  // (mirrors the backend CaseBody rule, eval#84).
  if (!outcomeHasCheck && !judge) {
    return {
      error:
        'A case must define at least one test: an outcome check (a write, a forbidden write, or an answer phrase) or a judge.',
    }
  }

  const body: CaseBody = {
    name: form.name.trim() || undefined,
    scenario: form.scenario.trim(),
    context: context as ContextMessage[],
  }

  // Omit the outcome block for judge-only cases — the backend treats it as
  // optional (eval#84).
  if (outcomeHasCheck) {
    body.outcome = outcome
  }

  if (form.goldenPromptId) {
    body.golden_prompt_id = form.goldenPromptId
  }

  if (form.trajectoryEnabled) {
    const trajectory: TrajectorySpec = {}
    if (form.mustCall.length > 0) {
      trajectory.must_call = form.mustCall
    }
    if (form.mustNotCall.length > 0) {
      trajectory.must_not_call = form.mustNotCall
    }
    if (form.maxToolCalls != null) {
      trajectory.max_tool_calls = form.maxToolCalls
    }
    body.trajectory = trajectory
  }

  if (judge) {
    body.judge = judge
  }

  return { body }
}

export const buildCaseCreateInput = (
  form: CaseFormState,
  body: CaseBody,
): EvalCaseCreateInput => ({
  name: form.name.trim(),
  body,
  tag_ids: form.tagIds,
})

export const buildCaseUpdateInput = (
  evalCase: EvalCase,
  body: CaseBody,
): EvalCaseUpdateInput => ({
  case_id: evalCase.case_id,
  body,
})

/** Build the version-free tag-replace payload (PUT /eval-cases/{id}/tags). */
export const buildCaseTagsUpdateInput = (
  evalCase: EvalCase,
  tagIds: string[],
): EvalCaseTagsUpdateInput => ({
  case_id: evalCase.case_id,
  tag_ids: tagIds,
})

/** Order-insensitive equality of two tag-id selections. */
export const tagIdsEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false
  }
  const sortedB = [...b].sort()
  return [...a].sort().every((id, i) => id === sortedB[i])
}

/**
 * Whether two prepared case bodies are identical. Both operands come from
 * `prepareCaseBody`, which builds keys in a fixed order, so a stable
 * stringify comparison is reliable — used to skip a body-versioning PUT when
 * only the (version-free) tags changed.
 */
export const caseBodyEqual = (a: CaseBody, b: CaseBody): boolean =>
  JSON.stringify(a) === JSON.stringify(b)

/** Case-insensitive, client-side name filter (the list endpoint returns all). */
export const filterCasesByName = (
  cases: EvalCase[],
  query: string,
): EvalCase[] => {
  const q = query.trim().toLowerCase()
  if (!q) {
    return cases
  }
  return cases.filter((c) => c.name.toLowerCase().includes(q))
}

/** Build a safe download filename from the case name. */
export const caseFilename = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `${slug || 'case'}.json`
}

/** Download a case's body as a JSON file named after the case. */
export const downloadCaseJson = (evalCase: EvalCase): void => {
  saveJson(evalCase.body, caseFilename(evalCase.name))
}
